import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tree, Typography, message } from 'antd';
import { AppstoreOutlined, ArrowDownOutlined, ArrowUpOutlined, FolderOutlined, HolderOutlined, LinkOutlined } from '@ant-design/icons';
import { canonicalTreeKey, referenceTree } from '../../permissions/catalog';
import { movableTreeNode, moveTreeNode, pointerDestination, stepDestination } from '../../permissions/movement';

const expandableKeys = nodes => nodes.flatMap(node => node.children?.length ? [node.key, ...expandableKeys(node.children)] : []);
export default function ResourceTree({ catalog, search, selected, onSelect, onChange, writable, disabled = false }) {
    const tree = useMemo(() => referenceTree(catalog, search), [catalog, search]);
    const [expanded, setExpanded] = useState(() => expandableKeys(tree));
    // New children and moves must remain visible even when their parent did not
    // exist at mount time. Preserve the user's other expanded/collapsed nodes.
    const selectedPath = selected ? canonicalTreeKey(catalog, selected) : '';
    useEffect(() => {
        if (!selectedPath) return;
        const parts = selectedPath.split('/');
        const ancestors = parts.slice(0, -1).map((_, index) => parts.slice(0, index + 1).join('/'));
        setExpanded(keys => ancestors.every(key => keys.includes(key)) ? keys : [...new Set([...keys, ...ancestors])]);
    }, [selectedPath]);
    const [dragging, setDragging] = useState('');
    const [preview, setPreview] = useState(null);
    const source = useRef(null);
    const expandTimer = useRef(null);
    const expandCandidate = useRef('');
    const dragEnabled = writable && !disabled && !search?.trim();
    const finishDrag = () => {
        source.current = null; setDragging(''); setPreview(null);
        clearTimeout(expandTimer.current); expandCandidate.current = '';
    };
    useEffect(() => {
        if (!dragEnabled) finishDrag();
        return () => clearTimeout(expandTimer.current);
    }, [dragEnabled]);
    const applyMove = (node, destination) => {
        if (!writable || disabled || !destination) return;
        try {
            const next = moveTreeNode(catalog, node, destination);
            if (next !== catalog) {
                const before = canonicalTreeKey(catalog, node.resource);
                const after = canonicalTreeKey(next, node.resource);
                setExpanded(keys => [...new Set([
                    ...keys.map(key => !node.referenceOwner && (key === before || key.startsWith(`${before}/`)) ? after + key.slice(before.length) : key),
                    ...(destination.parent ? [canonicalTreeKey(next, destination.parent)] : []),
                ])]);
                onChange(next); onSelect(node.resource);
            }
        } catch (error) { message.warning(error.message); }
    };
    const destinationAtPointer = (event, target) => {
        if (!dragEnabled) return null;
        const bounds = event.currentTarget.getBoundingClientRect();
        return pointerDestination(catalog, source.current, target, (event.clientY - bounds.top) / Math.max(bounds.height, 1));
    };
    const previewDrop = (event, node) => {
        event.preventDefault(); event.stopPropagation();
        const next = destinationAtPointer(event, node);
        setPreview(current => current?.key === next?.key && current?.mode === next?.mode ? current : next);
        if (event.dataTransfer) event.dataTransfer.dropEffect = next ? 'move' : 'none';
        const candidate = next?.mode === 'inside' ? node.key : '';
        if (expandCandidate.current !== candidate) {
            clearTimeout(expandTimer.current); expandCandidate.current = candidate;
            if (candidate && !expanded.includes(candidate)) expandTimer.current = setTimeout(() => {
                if (expandCandidate.current === candidate) setExpanded(keys => [...new Set([...keys, candidate])]);
            }, 550);
        }
    };
    return <>
        {writable && <Typography.Paragraph className="permission-move-hint" type="secondary">拖到行的上、下边缘调整顺序，拖到目录中间可移入。引用可与同级资源混排。{disabled ? '请先完成当前编辑。' : search?.trim() ? '清空搜索后可拖拽。' : ''}</Typography.Paragraph>}
        <Tree blockNode disabled={disabled} expandedKeys={search?.trim() ? [...new Set([...expanded, ...expandableKeys(tree)])] : expanded} onExpand={setExpanded}
            selectedKeys={selected ? [canonicalTreeKey(catalog, selected)] : []} treeData={tree}
            onSelect={(_, info) => !disabled && onSelect(info.node.resource || '')}
            titleRender={node => {
                const movable = writable && movableTreeNode(catalog, node);
                return <span className={`permission-drag-row${dragging === node.key ? ' permission-dragging' : ''}${preview?.key === node.key ? ` permission-drop-${preview.mode}` : ''}`}
                    data-tree-key={node.key} draggable={dragEnabled && movable}
                    onDragStart={event => {
                        event.stopPropagation();
                        if (!dragEnabled || !movable || event.target.closest('button')) { event.preventDefault(); return; }
                        source.current = node; setDragging(node.key);
                        if (event.dataTransfer) { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', node.key); }
                    }}
                    onDragEnd={event => { event.stopPropagation(); finishDrag(); }}
                    onDragOver={event => previewDrop(event, node)}
                    onDragLeave={event => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                            setPreview(current => current?.key === node.key ? null : current);
                            clearTimeout(expandTimer.current); expandCandidate.current = '';
                        }
                    }}
                    onDrop={event => {
                        event.preventDefault(); event.stopPropagation();
                        const dragged = source.current;
                        const target = destinationAtPointer(event, node);
                        finishDrag();
                        if (dragged && target) applyMove(dragged, target.destination);
                    }}>
                    {movable && <HolderOutlined className="permission-drag-handle" aria-label="拖动排序" />}
                    <span className={node.referenceOwner ? 'permission-tree-reference' : 'permission-tree-title'}>
                        {node.kind === 'directory' ? <FolderOutlined /> : <AppstoreOutlined />}
                        <span title={node.title}>{node.title}</span>
                        {node.referenceOwner && <LinkOutlined aria-label="引用" />}
                        <Typography.Text type="secondary">{node.referenceOwner ? '引用' : node.kind === 'directory' ? '目录' : '资源'}</Typography.Text>
                        {movable && <span className="permission-tree-actions" onMouseDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}>
                            <Button type="text" size="small" aria-label={`上移${node.referenceOwner ? '引用' : '节点'} ${node.title}`} title="上移" icon={<ArrowUpOutlined />} disabled={disabled || !stepDestination(catalog, node, -1)} onClick={() => applyMove(node, stepDestination(catalog, node, -1))} />
                            <Button type="text" size="small" aria-label={`下移${node.referenceOwner ? '引用' : '节点'} ${node.title}`} title="下移" icon={<ArrowDownOutlined />} disabled={disabled || !stepDestination(catalog, node, 1)} onClick={() => applyMove(node, stepDestination(catalog, node, 1))} />
                        </span>}
                    </span>
                </span>;
            }} />
    </>;
}
